import { useState, useEffect } from "react";
import { jwtFetch } from "./utils/apiClient";
import { useParams, useNavigate } from "react-router-dom";
import Menu from "./Components/Menu";
import Header from "./Components/Header";
import { config, encodeBase64RequestBody, numberFormat, priceFormat } from "./Components/Config";
import { format } from "date-fns";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";

const QuotationAtbDetail = () => {
  const { id_quotation, term_number } = useParams();
  const [ quotation, setQuotation ] = useState(null);
  const [ quotationItemList, setQuotationItemList ] = useState([]);
  const [ iouList, setIouList ] = useState(null);
  const [ iouTotal, setIouTotal ] = useState(0);
  const [ exchangeRate, setExchangeRate ] = useState(0);
  const [ subcompanyList, setSubcompanyList ] = useState(null);
  const [ subcompanyId, setSubcompanyId] = useState(null);
  const [ isEditable, setIsEditable ] = useState(true);
  const [ isEditing, setIsEditing ] = useState(true);
  
  const navigate = useNavigate();

  const attachmentTitles = [
    {value: "Signed Quotation", label: "Signed Quotation"},
    {value: "Customer PO", label: "Customer PO"},
    {value: "PKS", label: "PKS"},
    {value: "MSA", label: "MSA"},
    {value: "Agreement", label: "Agreement"},
    {value: "Copy of Customer Tax ID", label: "Copy of Customer Tax ID"},
    {value: "Official Report", label: "Official Report"},
    {value: "Certificate of Completion BAST", label: "Certificate of Completion BAST"},
  ];

  const getQuotation = async () => {
    try {
      const fetchGetQuotation = await jwtFetch(config.ENDPOINT + 'get/quotation', {
        method: 'POST',
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          id_quotation: id_quotation
        })
      });
      const responseGetQuotation = await fetchGetQuotation.json();
      if (responseGetQuotation.data) {
        setQuotationItemList([...quotationItemList, ...responseGetQuotation.data[0].quotation_item_list]);
        setQuotation(responseGetQuotation.data[0]);
      } else {
        alert(responseGetQuotation.message);
      }
    } catch (error) {
      console.error('Error in getQuotation:', error);
      alert('An error occurred: ' + (error.message || error));
    }
  };

  const getIouList = async () => {
    try {
      const requestIouList = {
        quotation_id: id_quotation,
        term_number: term_number
      }
      const fetchIouList = await jwtFetch(config.ENDPOINT + 'get/iou', {
        method: 'POST',
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestIouList)
      });
      const responseIouList = await fetchIouList.json();
      if (responseIouList.status === 200) {
        let total = 0;
        let ious = [];
        for (let i in responseIouList.data) {
          let iou = responseIouList.data[i];
          iou.action = 'Update';
          iou.request_invoice_pillar = iou.request_invoice_pillar || 'Non-Bundling';
          iou.request_invoice_type = iou.request_invoice_type || 'New Order';
          iou.request_invoice_company_name = iou.request_invoice_company_name || iou.subcompany_name;
          iou.request_invoice_company_address = iou.request_invoice_company_address || iou.subcompany_address;
          iou.request_invoice_company_phone = iou.request_invoice_company_phone || iou.subcompany_phone;
          total += iou.iou_grand_total;
          iou.iou_items = await getIouItemList(responseIouList.data[i].id_iou);
          const subcompany = subcompanyList.find(sub => sub.id_subcompany === iou.subcompany_id);
          iou.subcompany = subcompany;
          ious.push(iou);
          if (iou.iou_status === 'Notification Sent') {
            setIsEditable(false);
            setIsEditing(false);
          }
        }
        setIouList(ious);
        setIouTotal(total);
        setExchangeRate(responseIouList.data[0].iou_exchange_rate);
      } else {
        alert(responseIouList.message);
      }
    } catch (error) {
      console.error('Error in getIouList:', error);
      alert('An error occurred: ' + (error.message || error));
    }
  }

  const getIouItemList = async (iou_id) => {
    try {
      const requestIouList = {
        iou_id: iou_id
      }
      const fetchIouList = await jwtFetch(config.ENDPOINT + 'get/iou_item', {
        method: 'POST',
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestIouList)
      });
      const responseIouList = await fetchIouList.json();
      if (responseIouList.status === 200) {
        let iouItems = [];
        for (let i in responseIouList.data) {
          if (responseIouList.data[i].product_qty > 0) {
            let iouItem = responseIouList.data[i];
            const quotationItem = quotationItemList.find(product => product.product_id === parseInt(iouItem.product_id));
            iouItem.product_cost = iouItem.product_currency_original !== iouItem.product_currency ? (iouItem.product_cost_original * quotation.quotation_exchange_rate) : iouItem.product_cost_original;
            iouItem.product_price = iouItem.product_currency_original !== iouItem.product_currency ? (iouItem.product_price_original * quotation.quotation_exchange_rate) : iouItem.product_price_original;
            iouItem.product_subtotal = iouItem.product_price * iouItem.product_qty * iouItem.contract_long_period;
            iouItem.product_prorate = parseFloat((iouItem.product_qty / quotationItem.product_qty * 100).toFixed(3));
            iouItem.product_discount_price = parseFloat((quotationItem.product_discount_price * (iouItem.product_qty / quotationItem.product_qty) * (iouItem.contract_long_period / quotationItem.contract_long_period)).toFixed(3));
            iouItem.product_discount_rate = parseFloat((iouItem.product_discount_price / iouItem.product_subtotal * 100).toFixed(3));
            iouItem.product_total = iouItem.product_subtotal - iouItem.product_discount_price;
            iouItem.product_total_prorate = parseFloat((iouItem.product_subtotal / quotation.quotation_subtotal * 100).toFixed(3));
            iouItem.product_discount_prorate = iouItem.product_total_prorate * quotation.quotation_discount_price / 100;
            iouItem.product_grand_total = parseFloat((iouItem.product_total - iouItem.product_discount_prorate).toFixed(3));
            iouItem.product_margin_price = iouItem.product_grand_total - (iouItem.product_cost * iouItem.product_qty * iouItem.contract_long_period);
            iouItem.product_margin_rate = parseFloat((iouItem.product_margin_price / iouItem.product_grand_total * 100).toFixed(3));
            iouItem.action = 'Update';
            iouItems.push(iouItem);
          }
        }
        return iouItems;
      } else {
        alert(responseIouList.message);
      }
    } catch (error) {
      console.error('Error in getIouItemList:', error);
      alert('An error occurred: ' + (error.message || error));
    }
  }

  const getSubcompanyList = async () => {
    try {
      const requestSubcompanyList = {
        company_id: quotation.company_id
      }
      const fetchSubcompanyList = await jwtFetch(config.ENDPOINT + 'get/subcompany', {
        method: 'POST',
        headers: {
          "Content-Type": "application/json",
        },
        body: encodeBase64RequestBody(requestSubcompanyList)
      });
      const responseSubcompanyList = await fetchSubcompanyList.json();
      if (responseSubcompanyList.status === 200) {
        setSubcompanyList(responseSubcompanyList.data);
      } else {
        alert(responseSubcompanyList.message);
      }
    } catch (error) {
      console.error('Error in getSubcompanyList:', error);
      alert('An error occurred: ' + (error.message || error));
    }
  }

  const changeIouData = (iouIndex, column, value) => {
    let iou = iouList[iouIndex];
    iou[column] = value;
    setIouList([...iouList]);
  }

  const changeIouItemProduct = (iouIndex, iouItemIndex, productId) => {
    let iouItem = iouList[iouIndex].iou_items[iouItemIndex];
    const quotationItem = quotationItemList.find(product => product.product_id === parseInt(productId));
    iouItem.product_id = quotationItem.product_id;
    iouItem.product_display_name = quotationItem.product_display_name;
    iouItem.product_display_description = quotationItem.product_display_description;
    iouItem.product_type = quotationItem.product_type;
    iouItem.product_domain = quotationItem.product_domain;
    iouItem.product_qty = 0;
    iouItem.service_type = quotationItem.service_type;
    iouItem.contract_long_period = iouList[0].number_of_months;
    iouItem.contract_duration_period = quotationItem.contract_duration_period;
    iouItem.product_prorate = parseFloat((iouItem.product_qty / quotationItem.product_qty * 100).toFixed(3));
    iouItem.product_currency_original = quotationItem.product_currency_original;
    iouItem.product_currency = quotationItem.product_currency;
    iouItem.product_cost_original = quotationItem.product_cost_original;
    iouItem.product_cost = iouItem.product_currecy_original !== iouItem.product_currency ? (iouItem.product_cost_original * quotation.quotation_exchange_rate) : iouItem.product_cost_original;
    iouItem.product_price_original = quotationItem.product_price_original;
    iouItem.product_price = iouItem.product_currecy_original !== iouItem.product_currency ? (iouItem.product_price_original * quotation.quotation_exchange_rate) : iouItem.product_price_original;
    iouItem.product_subtotal = iouItem.product_price * iouItem.product_qty * iouItem.contract_long_period;
    iouItem.product_discount_price = parseFloat((quotationItem.product_discount_price * (iouItem.product_qty / quotationItem.product_qty) * (iouItem.contract_long_period / quotationItem.contract_long_period)).toFixed(3));
    iouItem.product_discount_rate = parseFloat((iouItem.product_discount_price / iouItem.product_subtotal * 100).toFixed(3));
    iouItem.product_total = iouItem.product_subtotal - iouItem.product_discount_price;
    iouItem.product_total_prorate = parseFloat((iouItem.product_subtotal / quotation.quotation_subtotal * 100).toFixed(3));
    iouItem.product_discount_prorate = iouItem.product_total_prorate * quotation.quotation_discount_price / 100;
    iouItem.product_grand_total = parseFloat((iouItem.product_total - iouItem.product_discount_prorate).toFixed(3));
    iouItem.product_active_licence_date = quotationItem.product_active_licence_date;
    iouItem.product_margin_price = iouItem.product_grand_total - (iouItem.product_cost * iouItem.product_qty * iouItem.contract_long_period);
    iouItem.product_margin_rate = parseFloat((iouItem.product_margin_price / iouItem.product_grand_total * 100).toFixed(3));
        
    setIouList([...iouList]);
  }

  const changeIouItemQty = (iouIndex, iouItemIndex, qty) => {
    let iou = iouList[iouIndex];
    let iouItem = iou.iou_items[iouItemIndex];
    let quotationItem = quotation.quotation_items.find(quotation_item => parseInt(quotation_item.product_id) === parseInt(iouItem.product_id));
    iouItem.product_qty = parseInt(qty);
    iouItem.product_cost = iouItem.product_currecy_original !== iouItem.product_currency ? (iouItem.product_cost_original * quotation.quotation_exchange_rate) : iouItem.product_cost_original;
    iouItem.product_price = iouItem.product_currecy_original !== iouItem.product_currency ? (iouItem.product_price_original * quotation.quotation_exchange_rate) : iouItem.product_price_original;
    iouItem.product_subtotal = iouItem.product_price * iouItem.product_qty * iouItem.contract_long_period;
    iouItem.product_prorate = parseFloat((iouItem.product_qty / quotationItem.product_qty * 100).toFixed(3));
    iouItem.product_discount_price = parseFloat((quotationItem.product_discount_price * (iouItem.product_qty / quotationItem.product_qty) * (iouItem.contract_long_period / quotationItem.contract_long_period)).toFixed(3));
    iouItem.product_discount_rate = parseFloat((iouItem.product_discount_price / iouItem.subtotal * 100).toFixed(3));
    iouItem.product_total = iouItem.product_subtotal - iouItem.product_discount_price;
    iouItem.product_total_prorate = parseFloat((iouItem.product_subtotal / quotation.quotation_subtotal * 100).toFixed(3));
    iouItem.product_discount_prorate = iouItem.product_total_prorate * quotation.quotation_discount_price / 100;
    iouItem.product_grand_total = parseFloat((iouItem.product_total - iouItem.product_discount_prorate).toFixed(3));
    iouItem.product_margin_price = iouItem.product_grand_total - (iouItem.product_cost * iouItem.product_qty * iouItem.contract_long_period);
    iouItem.product_margin_rate = parseFloat((iouItem.product_margin_price / iouItem.product_grand_total * 100).toFixed(3));

    setIouList([...iouList]);
  }

  const addIouItemProduct = (iouIndex) => {
    const iou = iouList[iouIndex];
    const iouItem = {
      "iou_id": iou.id_iou,
      "product_id": '',
      "product_display_name": '',
      "product_display_description": '',
      "product_type": '',
      "product_domain": '',
      "product_qty": 0,
      "product_prorate": 0,
      "product_currency_original": '',
      "product_currency": '',
      "product_cost_original": 0,
      "product_cost": 0,
      "product_price_original": 0,
      "product_price": 0,
      "product_subtotal": 0,
      "product_discount_rate": 0,
      "product_discount_price": 0,
      "product_discount_prorate": 0,
      "product_total": 0,
      "product_total_prorate": 0,
      "product_grand_total": 0,
      "product_active_licence_date": '',
      "service_type": '',
      "contract_long_period": 0,
      "contract_duration_period": '',
      "action": 'Create'
    }
    iou.iou_items.push(iouItem);
    setIouList([...iouList]);
  }

  const removeIouItemProduct = (iouIndex, iouItemIndex) => {
    const iouItems = iouList[iouIndex].iou_items;
    if (iouItems[iouItemIndex].id_iou_item) {
      iouItems[iouItemIndex].action = 'Delete';
      iouItems[iouItemIndex].product_qty = 0;
    } else {
      iouItems.splice(iouItemIndex, 1);
    }
    setIouList([...iouList]);
  }

  const addIou = () => {
    const subcompany = subcompanyList.find(sub => sub.id_subcompany === subcompanyId.value);
    const ious = iouList;
    const newIou = {
      "id_iou": '0',
      "quotation_id": ious[0].quotation_id,
      "so_id": ious[0].so_id,
      "company_id": ious[0].company_id,
      "subcompany": subcompany,
      "subcompany_id": subcompanyId.value,
      "subcompany_name": subcompanyId.label,
      "term_number": term_number,
      "number_of_months": ious[0].number_of_months,
      "iou_year": ious[0].iou_year,
      "iou_currency": ious[0].iou_currency,
      "iou_exchange_rate": ious[0].iou_exchange_rate,
      "iou_tax_price": 0,
      "iou_tax_rate": ious[0].iou_tax_rate,
      "iou_items": [],
      "iou_subtotal": 0,
      "iou_grand_total": 0,
      "iou_status": "New",
      "start_term_period": ious[0].start_term_period,
      "end_term_period": ious[0].end_term_period,
      "request_invoice_by": null,
      "request_invoice_date": null,
      "request_invoice_pillar": 'Non-Bundling',
      "request_invoice_type": 'New Order',
      "request_invoice_documents": [],
      "request_invoice_company_name": subcompany.company_name,
      "request_invoice_company_address": subcompany.company_address,
      "request_invoice_company_npwp": '',
      "request_invoice_company_phone": subcompany.company_phone,
      "request_invoice_company_fax": '',
      "request_invoice_contact_name": '',
      "request_invoice_contact_position": '',
      "request_invoice_attachments": [],
      "request_invoice_delivery": '',
      "request_invoice_remarks": '',
      "sales_name": ious[0].sales_name,
      "created_on": ious[0].created_on,
      "created_by": ious[0].created_by,
      "action": 'Create'
    }
    ious.push(newIou);
    setIouList([...iouList]);
  }

  const addIouAttachment = (iouIndex) => {
    let iou = iouList[iouIndex];
    iou.request_invoice_attachments = {
      "attachment_title": '',
      "attachment_status": '',
      "attachment_file": null,
      "attachment_link": '',
    };
    setIouList([...iouList]);
  }

  const removeIouAttachment = () => {
    console.log('ok');
  }

  const changeIouAttachmentData = () => {
    console.log('ok');
  }

  const removeIou = (iouIndex) => {
    // const ious = iouList;
    // ious.splice(iouIndex, 1);
    const iou = iouList[iouIndex];
    iou.iou_items = [];
    iou.iou_status = 'Canceled';
    setIouList([...iouList]);
  }

  const validateQuantities = () => {
    const quotationQuantities = new Map();
    const iouQuantities = new Map();

    quotationItemList.forEach(item => {
      const currentQty = quotationQuantities.get(item.product_display_name) || 0;
      quotationQuantities.set(item.product_display_name, currentQty + item.product_qty);
    });

    iouList.forEach(iou => {
      iou.iou_items.forEach(item => {
        const currentQty = iouQuantities.get(item.product_display_name) || 0;
        iouQuantities.set(item.product_display_name, currentQty + item.product_qty);
      });
    });

    let isValid = {
      "status": true,
      "iou_index": "",
      "iou_item_index": "",
    };

    quotationQuantities.forEach((quotationQty, product_display_name) => {
      const iouQty = iouQuantities.get(product_display_name) || 0;
      if (quotationQty !== iouQty) {
        isValid.status = false;
        isValid.message = "Total quantity of " + product_display_name + " must be " + quotationQty + '.';
      }
    });

    return isValid;
  };

  const updateAtb = async () => {
    try {
      let ious = iouList;
      for (let iouIndex in ious) {
        let iouSubtotal = 0;
        for (let iouItemIndex in ious[iouIndex].iou_items) {
          ious[iouIndex].iou_items[iouItemIndex].product_active_licence_date = format(new Date(ious[iouIndex].iou_items[iouItemIndex].product_active_licence_date), 'yyyy-MM-dd');
          if (ious[iouIndex].iou_items[iouItemIndex].action === 'Delete') {
            ious[iouIndex].iou_items[iouItemIndex].action = 'Update';
          }
          iouSubtotal += ious[iouIndex].iou_items[iouItemIndex].product_grand_total;
        }
        ious[iouIndex].iou_exchange_rate = exchangeRate;
        ious[iouIndex].iou_subtotal = iouSubtotal;
        ious[iouIndex].iou_tax_price = parseFloat((iouSubtotal * 12 / 100).toFixed(3));
        ious[iouIndex].iou_grand_total = iouSubtotal + ious[iouIndex].iou_tax_price;
        ious[iouIndex].start_term_period = format(ious[iouIndex].start_term_period, 'yyyy-MM-dd');
        ious[iouIndex].end_term_period = format(ious[iouIndex].end_term_period, 'yyyy-MM-dd');
        ious[iouIndex].created_on = format(ious[iouIndex].created_on, 'yyyy-MM-dd HH:mm:ss');
      }

      const isValid = validateQuantities();
      if (isValid.status === false) {
        alert(isValid.message);
        return;
      }

      const fetchUpdateAtb = await jwtFetch(config.ENDPOINT + 'update/atb', {
        method: 'POST',
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(ious)
      });
      const responseUpdateAtb = await fetchUpdateAtb.json();
      if (responseUpdateAtb.status === 200) {
        window.location.reload();
      } else {
        alert(responseUpdateAtb.message);
      }
    } catch (error) {
      console.error('Error in updateAtb:', error);
      alert('An error occurred: ' + (error.message || error));
    }
  }

  const sendAtb = async () => {
    try {
      const crmUser = JSON.parse(localStorage.getItem('crmUser'));
      let ious = iouList;
      for (let iouIndex in ious) {
        const requestEditIouStatus = {
          id_iou: ious[iouIndex].id_iou,
          iou_status: 'Notification Sent',
          updated_on: format(new Date(), 'yyyy-MM-dd HH:mm:ss'),
          updated_by: crmUser.user_keyid,
        };
        requestEditIouStatus.request_invoice_date = format(new Date(), 'yyyy-MM-dd HH:mm:ss');
        requestEditIouStatus.request_invoice_by = crmUser.user_keyid;
        requestEditIouStatus.request_invoice_remarks = ious[iouIndex].request_invoice_remarks;
        await jwtFetch(config.ENDPOINT + 'update/iou_status', {
          method: 'POST',
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestEditIouStatus)
        });
      }
      window.location.href = '/quotation/detail/' + id_quotation;
    } catch (error) {
      console.error('Error in sendAtb:', error);
      alert('An error occurred: ' + (error.message || error));
    }
  }

  useEffect(() => {
    getQuotation();
  }, []);

  useEffect(() => {
    if (quotation) {
      getSubcompanyList();
    }
  }, [quotation]);

  useEffect(() => {
    if (subcompanyList) {
      getIouList();
    }
  }, [subcompanyList]);

  useEffect(() => {
    if (iouList && quotation && exchangeRate) {
      const updatedIouList = iouList;
      for (let iouIndex in updatedIouList) {
        let iouSubtotal = 0;

        for (let iouItemIndex in updatedIouList[iouIndex].iou_items) {
          let iouItem = updatedIouList[iouIndex].iou_items[iouItemIndex];
          if (iouItem.product_id) {
            let quotationItem = quotation.quotation_items.find(item => parseInt(item.product_id) === parseInt(iouItem.product_id));
            iouItem.product_cost = iouItem.product_currency_original !== iouItem.product_currency ? (iouItem.product_cost_original * exchangeRate) : iouItem.product_cost_original;
            iouItem.product_price = iouItem.product_currency_original !== iouItem.product_currency ? (iouItem.product_price_original * exchangeRate) : iouItem.product_price_original;
            iouItem.product_subtotal = iouItem.product_price * iouItem.product_qty * iouItem.contract_long_period;
            iouItem.product_prorate = parseFloat((iouItem.product_qty / quotationItem.product_qty * 100).toFixed(3));
            iouItem.product_discount_price = parseFloat((quotationItem.product_discount_price * (iouItem.product_qty / quotationItem.product_qty) * (iouItem.contract_long_period / quotationItem.contract_long_period)).toFixed(3));
            iouItem.product_discount_rate = iouItem.product_subtotal ? parseFloat((iouItem.product_discount_price / iouItem.product_subtotal * 100).toFixed(3)) : 0;
            iouItem.product_total = iouItem.product_subtotal - iouItem.product_discount_price;
            iouItem.product_total_prorate = iouItem.product_subtotal > 0 ? parseFloat((iouItem.product_subtotal / quotation.quotation_subtotal * 100).toFixed(3)) : 0;
            iouItem.product_discount_prorate = iouItem.product_total_prorate * quotation.quotation_discount_price / 100;
            iouItem.product_grand_total = parseFloat((iouItem.product_total - iouItem.product_discount_prorate).toFixed(3));
            iouItem.product_margin_price = iouItem.product_grand_total - (iouItem.product_cost * iouItem.product_qty * iouItem.contract_long_period);
            iouItem.product_margin_rate = iouItem.product_grand_total > 0 ? parseFloat((iouItem.product_margin_price / iouItem.product_grand_total * 100).toFixed(3)) : 0;

            iouSubtotal += iouItem.product_grand_total;
          }
        }

        updatedIouList[iouIndex].iou_exchange_rate = exchangeRate;
        updatedIouList[iouIndex].iou_subtotal = iouSubtotal;
        updatedIouList[iouIndex].iou_tax_price = parseFloat((iouSubtotal * 12 / 100).toFixed(3));
        updatedIouList[iouIndex].iou_grand_total = iouSubtotal + updatedIouList[iouIndex].iou_tax_price;
      }

      if (JSON.stringify(updatedIouList) !== JSON.stringify(iouList)) {
        setIouList(updatedIouList);
      }
    }

  }, [quotation, iouList, exchangeRate]);

  return (
    <div id="quotation-atb">
      <div className="container-fluid p-0">
        <div className="row g-0 mh-100vh">
          <div className="col-md-2 border-end">
            <div className="d-md-flex d-none align-items-center justify-content-center p-1 h-48">
              <a className="h-100" href="/dashboard"><img className="h-100" src="/cuancuan.png" alt="PointStar"/></a>
            </div>
            <hr className="m-0"/>
            <Menu active="quotation"/>
          </div>
          <div className="col-md-10">
            <Header/>
            <hr className="m-0"/>
            <div className="p-4">
              {quotation && iouList && (
                <div className="mb-4">
                  <div className="d-flex align-items-center justify-content-between mb-3">                    
                    <div className="d-flex align-items-center">
                      <div className="fs-24"><button className="btn btn-link" onClick={() => navigate(-1)}><i className="bi bi-arrow-left"/></button> Invoice Request</div>
                      <span className={"badge badge-sm fs-16 ms-3 text-light text-bg-success"}>Notification Sent</span>
                    </div>
                    {!isEditing && isEditable && (
                      <button className="btn btn-sm btn-outline-secondary" onClick={() => setIsEditing(true)}><i className="bi bi-pencil"/> Edit</button>
                    )}                    
                  </div>
                  <div className="row">
                    <div className="col-md-6">
                      <div className="row mb-2">
                        <div className="col-md-4 text-secondary">Quotation</div>
                        <div className="col-md-8">{quotation.quotation_code} <a href={`/quotation/detail/${quotation.id_quotation}`}><i className="bi bi-link-45deg"/></a></div>
                      </div>
                      <div className="row mb-2">
                        <div className="col-md-4 text-secondary">Sales Order</div>
                        <div className="col-md-8">{iouList[0].so_code} <a href={`/salesorder/detail/${iouList[0].so_code}`}><i className="bi bi-link-45deg"/></a></div>
                      </div>
                      <div className="row mb-2">
                        <div className="col-md-4 text-secondary">Company Group</div>
                        <div className="col-md-8">{quotation.company_name} <a href={`/client/detail/${quotation.company_id}`}><i className="bi bi-link-45deg"/></a></div>
                      </div>
                      <div className="row mb-2">
                        <div className="col-md-4 text-secondary">Schedule</div>
                        <div className="col-md-8">{term_number.replace('Term', 'Schedule')}</div>
                      </div>
                      <div className="row mb-2">
                        <div className="col-md-4 text-secondary">Period Date</div>
                        <div className="col-md-8">{iouList[0].start_term_period === iouList[0].end_term_period ? format(iouList[0].start_term_period, 'd MMM yyyy') : (format(iouList[0].start_term_period, 'd MMM yyyy') + " - " + format(iouList[0].end_term_period, 'dd MMM yyyy'))}</div>
                      </div>
                      <div className="row mb-2">
                        <div className="col-md-4 text-secondary">Currency</div>
                        <div className="col-md-8">{iouList[0].iou_currency}</div>
                      </div>
                      <div className="row align-items-baseline mb-2">
                        <div className="col-md-4 text-secondary">Exchange Rate</div>
                        <div className="col-md-8">1 USD <i className="bi bi-arrow-right mx-1"/> {priceFormat(exchangeRate, iouList[0].iou_currency)} ({quotation && quotation.quotation_exchange_floating})</div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="mb-4">
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <div className="fs-24">Subsidiary List</div>
                </div>
                {iouList ? iouList.map((iou, i) =>
                  <div className={"card mb-4"} key={"iou-" + i}>
                    <div className="card-body">
                      <div className="d-flex align-items-center justify-content-between mb-3">
                        <div className="fs-18"><strong>Subsidiary #1 &emsp;|&emsp; Informa</strong></div>
                      </div>
                      <div className="row mb-2">
                        <div className="col-md-6">
                          <div className="card mb-2">
                            <div className="card-body">
                              <div className="d-flex align-items-center justify-content-between mb-3">
                                <div className="fs-14"><strong>Billing To</strong></div>
                              </div>
                              <div className="row mb-2">
                                <div className="col-md-4 text-secondary">Legal Name</div>
                                <div className="col-md-8">{iou.subcompany.company_billing_name}</div>
                              </div>
                              <div className="row mb-2">
                                <div className="col-md-4 text-secondary">Billing Address</div>
                                <div className="col-md-8">{iou.subcompany.company_address}</div>
                              </div>
                              <div className="row mb-2">
                                <div className="col-md-4 text-secondary">Mailing Address</div>
                                <div className="col-md-8">{iou.subcompany.company_address}</div>
                              </div>
                              <div className="row mb-2">
                                <div className="col-md-4 text-secondary">Phone Number</div>
                                <div className="col-md-8">{iou.subcompany.company_phone}</div>
                              </div>
                              <div className="row mb-2">
                                <div className="col-md-4 text-secondary">Fax Number</div>
                                <div className="col-md-8">{iou.subcompany.company_fax}</div>
                              </div>
                              <div className="row mb-2">
                                <div className="col-md-4 text-secondary">NPWP</div>
                                <div className="col-md-8">
                                  <ul className="ps-0 mb-0">
                                    {iou.subcompany.company_npwp && JSON.parse(iou.subcompany.company_npwp).map((npwp, n) =>
                                      <li className="ellipsis" key={"npwp-" + n}>{npwp.fileUrl}</li>
                                    )}
                                  </ul>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="table-responsive">
                        <table className="table table-hover">
                          <thead>
                            <tr>
                              <th className="bg-secondary text-light">#</th>
                              <th className="bg-secondary text-light">SKUs</th>
                              <th className="bg-secondary text-light" style={{textAlign: 'center'}}>Qty</th>
                              <th className="bg-secondary text-light" style={{textAlign: 'center'}}>Unit</th>
                              <th className="bg-secondary text-light" style={{textAlign: 'center'}}>Currency</th>
                              <th className="bg-secondary text-light" style={{textAlign: 'center'}}>Unit Price</th>
                              <th className="bg-secondary text-light" style={{textAlign: 'center'}}>Period</th>
                              <th className="bg-secondary text-light" style={{textAlign: 'center'}}>Selling Price</th>
                              <th className="bg-secondary text-light" style={{textAlign: 'right'}}>Amount</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td valign="middle">1</td>
                              <td valign="middle">Google Workspace Business Starter</td>
                              <td align="center" valign="middle">30</td>
                              <td align="center" valign="middle">Seat</td>
                              <td align="center" valign="middle">USD</td>
                              <td align="center" valign="middle">8</td>
                              <td align="center" valign="middle">3 Months</td>
                              <td align="center" valign="middle">132,440 IDR</td>
                              <td align="right" valign="middle">11,154,020 IDR</td>
                            </tr>
                            <tr>
                              <td valign="middle">2</td>
                              <td valign="middle">Google Workspace Business Srandard</td>
                              <td align="center" valign="middle">8</td>
                              <td align="center" valign="middle">Seat</td>
                              <td align="center" valign="middle">USD</td>
                              <td align="center" valign="middle">12</td>
                              <td align="center" valign="middle">3 Months</td>
                              <td align="center" valign="middle">198,660 IDR</td>
                              <td align="right" valign="middle">4,511,608 IDR</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                      <div className="row mb-2">
                        <div className="col-md-6">
                          <div className="row">
                            <div className="col-md-4 text-secondary">Total Amount</div>
                            <div className="col-md-8">{priceFormat(iou.iou_items.reduce((sum, iouItem) => sum + parseInt(iouItem.product_grand_total), 0), iou.iou_currency)}</div>
                          </div>
                        </div>
                      </div>
                      <div className="row mb-4">
                        <div className="col-md-12">
                          <div className="row d-flex align-items-baseline mb-2">
                            <div className="col-md-2 text-secondary">Documents</div>
                            <div className="col-md-8">Invoice, Faktur Pajak, Receipt/Kwitansi</div>
                          </div>
                          <div className="row mb-2">
                            <div className="col-md-2 text-secondary">Attachments</div>
                            <div className="col-md-10">
                              <div className="table-responsive">
                                <table className="table table-hover">
                                  <thead>
                                    <tr>
                                      <th className="bg-secondary text-light">#</th>
                                      <th className="bg-secondary text-light w-400">Title</th>
                                      <th className="bg-secondary text-light w-200" style={{textAlign: 'center'}}>Status</th>
                                      <th className="bg-secondary text-light" style={{textAlign: 'center'}}>File</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    <tr>
                                      <td valign="middle">1</td>
                                      <td valign="middle">Signed Quotation</td>
                                      <td align="center" valign="middle">Ada &amp; Terlampir</td>
                                      <td align="center" valign="middle"><a className="ellipsis" href="https://drive.google.com/file/d/15Q0YKNQevWyDHWwPyv80Uk0yvmdakzL0/preview" target="_blank" rel="noopener noreferrer">https://drive.google.com/file/d/10yvmdakzL05Q0Y...</a></td>
                                    </tr>
                                    <tr>
                                      <td valign="middle">2</td>
                                      <td valign="middle">MSA</td>
                                      <td align="center" valign="middle">Ada &amp; Terlampir</td>
                                      <td align="center" valign="middle"><a className="ellipsis" href="https://drive.google.com/file/d/15Q0YKNQevWyDHWwPyv80Uk0yvmdakzL0/preview" target="_blank" rel="noopener noreferrer">https://drive.google.com/file/d/1YKNQevWyDHWPy...</a></td>
                                    </tr>
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </div>
                          <div className="row mb-2">
                            <div className="col-md-2 text-secondary">Notes</div>
                            <div className="col-md-10">Ok please proceed to create invoice</div>
                          </div>
                        </div>
                      </div>

                      <div className="mb-2">
                        <div className="d-flex align-items-center justify-content-between mb-3">
                          <div className="fs-24">Invoice</div>
                        </div>
                        <div className="row">
                          <div className="col-md-6">
                            <div className="row align-items-baseline form-group mb-2">
                              <div className="col-md-4">
                                <label>Number</label>
                              </div>
                              <div className="col-md-8">
                                <input className="form-control" type="text" required/>
                              </div>
                            </div>
                            <div className="row align-items-baseline form-group mb-2">
                              <div className="col-md-4">
                                <label>Files</label>
                              </div>
                              <div className="col-md-8">
                                <input className="form-control" type="file" multiple={true} accept=".pdf, .png, .jpg, .jpeg, .gif, .webp, .svg, .bmp, .avif, .csv, .xls, .xlsx" required/>
                              </div>
                            </div>
                            <div className="row align-items-baseline form-group mb-2">
                              <div className="col-md-4">
                                <label>Remarks</label>
                              </div>
                              <div className="col-md-8">
                                <textarea className="form-control" rows="4"/>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                    </div>
                  </div>
                ) : (<p>No Account data</p>)}
                {iouList ? iouList.map((iou, i) =>
                  <div className={"card mb-4"} key={"iou-" + i}>
                    <div className="card-body">
                      <div className="d-flex align-items-center justify-content-between mb-3">
                        <div className="fs-18"><strong>Subsidiary #2 &emsp;|&emsp; Ace Hardware</strong></div>
                      </div>
                      <div className="row mb-2">
                        <div className="col-md-6">
                          <div className="card mb-2">
                            <div className="card-body">
                              <div className="d-flex align-items-center justify-content-between mb-3">
                                <div className="fs-14"><strong>Billing To</strong></div>
                              </div>
                              <div className="row mb-2">
                                <div className="col-md-4 text-secondary">Legal Name</div>
                                <div className="col-md-8">{iou.subcompany.company_billing_name}</div>
                              </div>
                              <div className="row mb-2">
                                <div className="col-md-4 text-secondary">Billing Address</div>
                                <div className="col-md-8">{iou.subcompany.company_address}</div>
                              </div>
                              <div className="row mb-2">
                                <div className="col-md-4 text-secondary">Mailing Address</div>
                                <div className="col-md-8">{iou.subcompany.company_address}</div>
                              </div>
                              <div className="row mb-2">
                                <div className="col-md-4 text-secondary">Phone Number</div>
                                <div className="col-md-8">{iou.subcompany.company_phone}</div>
                              </div>
                              <div className="row mb-2">
                                <div className="col-md-4 text-secondary">Fax Number</div>
                                <div className="col-md-8">{iou.subcompany.company_fax}</div>
                              </div>
                              <div className="row mb-2">
                                <div className="col-md-4 text-secondary">NPWP</div>
                                <div className="col-md-8">
                                  <ul className="ps-0 mb-0">
                                    {iou.subcompany.company_npwp && JSON.parse(iou.subcompany.company_npwp).map((npwp, n) =>
                                      <li className="ellipsis" key={"npwp-" + n}>{npwp.fileUrl}</li>
                                    )}
                                  </ul>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="table-responsive">
                        <table className="table table-hover">
                          <thead>
                            <tr>
                              <th className="bg-secondary text-light">#</th>
                              <th className="bg-secondary text-light">SKUs</th>
                              <th className="bg-secondary text-light" style={{textAlign: 'center'}}>Qty</th>
                              <th className="bg-secondary text-light" style={{textAlign: 'center'}}>Unit</th>
                              <th className="bg-secondary text-light" style={{textAlign: 'center'}}>Currency</th>
                              <th className="bg-secondary text-light" style={{textAlign: 'center'}}>Unit Price</th>
                              <th className="bg-secondary text-light" style={{textAlign: 'center'}}>Period</th>
                              <th className="bg-secondary text-light" style={{textAlign: 'center'}}>Selling Price</th>
                              <th className="bg-secondary text-light" style={{textAlign: 'right'}}>Amount</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td valign="middle">1</td>
                              <td valign="middle">Google Workspace Business Starter</td>
                              <td align="center" valign="middle">30</td>
                              <td align="center" valign="middle">Seat</td>
                              <td align="center" valign="middle">USD</td>
                              <td align="center" valign="middle">8</td>
                              <td align="center" valign="middle">3 Months</td>
                              <td align="center" valign="middle">132,440 IDR</td>
                              <td align="right" valign="middle">11,154,020 IDR</td>
                            </tr>
                            <tr>
                              <td valign="middle">2</td>
                              <td valign="middle">Google Workspace Business Srandard</td>
                              <td align="center" valign="middle">8</td>
                              <td align="center" valign="middle">Seat</td>
                              <td align="center" valign="middle">USD</td>
                              <td align="center" valign="middle">12</td>
                              <td align="center" valign="middle">3 Months</td>
                              <td align="center" valign="middle">198,660 IDR</td>
                              <td align="right" valign="middle">4,511,608 IDR</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                      <div className="row mb-2">
                        <div className="col-md-6">
                          <div className="row">
                            <div className="col-md-4 text-secondary">Total Amount</div>
                            <div className="col-md-8">{priceFormat(iou.iou_items.reduce((sum, iouItem) => sum + parseInt(iouItem.product_grand_total), 0), iou.iou_currency)}</div>
                          </div>
                        </div>
                      </div>
                      <div className="row mb-4">
                        <div className="col-md-12">
                          <div className="row d-flex align-items-baseline mb-2">
                            <div className="col-md-2 text-secondary">Documents</div>
                            <div className="col-md-8">Invoice, Faktur Pajak, Receipt/Kwitansi</div>
                          </div>
                          <div className="row mb-2">
                            <div className="col-md-2 text-secondary">Attachments</div>
                            <div className="col-md-10">
                              <div className="table-responsive">
                                <table className="table table-hover">
                                  <thead>
                                    <tr>
                                      <th className="bg-secondary text-light">#</th>
                                      <th className="bg-secondary text-light w-400">Title</th>
                                      <th className="bg-secondary text-light w-200" style={{textAlign: 'center'}}>Status</th>
                                      <th className="bg-secondary text-light" style={{textAlign: 'center'}}>File</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    <tr>
                                      <td valign="middle">1</td>
                                      <td valign="middle">Signed Quotation</td>
                                      <td align="center" valign="middle">Ada &amp; Terlampir</td>
                                      <td align="center" valign="middle"><a className="ellipsis" href="https://drive.google.com/file/d/15Q0YKNQevWyDHWwPyv80Uk0yvmdakzL0/preview" target="_blank" rel="noopener noreferrer">https://drive.google.com/file/d/10yvmdakzL05Q0Y...</a></td>
                                    </tr>
                                    <tr>
                                      <td valign="middle">2</td>
                                      <td valign="middle">MSA</td>
                                      <td align="center" valign="middle">Ada &amp; Terlampir</td>
                                      <td align="center" valign="middle"><a className="ellipsis" href="https://drive.google.com/file/d/15Q0YKNQevWyDHWwPyv80Uk0yvmdakzL0/preview" target="_blank" rel="noopener noreferrer">https://drive.google.com/file/d/1YKNQevWyDHWPy...</a></td>
                                    </tr>
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </div>
                          <div className="row mb-2">
                            <div className="col-md-2 text-secondary">Notes</div>
                            <div className="col-md-10">Ok please proceed to create invoice</div>
                          </div>
                        </div>
                      </div>

                      <div className="mb-2">
                        <div className="d-flex align-items-center justify-content-between mb-3">
                          <div className="fs-24">Invoice</div>
                        </div>
                        <div className="row">
                          <div className="col-md-6">
                            <div className="row align-items-baseline form-group mb-2">
                              <div className="col-md-4">
                                <label>Number</label>
                              </div>
                              <div className="col-md-8">
                                <input className="form-control" type="text" required/>
                              </div>
                            </div>
                            <div className="row align-items-baseline form-group mb-2">
                              <div className="col-md-4">
                                <label>Files</label>
                              </div>
                              <div className="col-md-8">
                                <input className="form-control" type="file" multiple={true} accept=".pdf, .png, .jpg, .jpeg, .gif, .webp, .svg, .bmp, .avif, .csv, .xls, .xlsx" required/>
                              </div>
                            </div>
                            <div className="row align-items-baseline form-group mb-2">
                              <div className="col-md-4">
                                <label>Remarks</label>
                              </div>
                              <div className="col-md-8">
                                <textarea className="form-control" rows="4"/>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                    </div>
                  </div>
                ) : (<p>No Account data</p>)}
              </div>

              <div className="mb-4">
                <div className="d-flex justify-content-end">
                  <button className="btn btn-primary ms-2" type="button" >Save</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuotationAtbDetail;


